import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
  type WorkerRequirementIncludedAutomatedNameUpdateType,
  type WorkerRequirementIncludedRequirementMappingType,
  type WorkerRequirementIncludedRequirementType,
  type WorkerRequirementsIncludedDocumentsType,
} from "@clipboard-health/contract-worker-app-bff";

import { type PrerequisiteAction, type WorkerRequirement } from "./types";

/**
 * Merges all worker requirements with their included resource details from the API response.
 * @param data - The full API response containing the requirements.
 * @returns An array of objects that combine each worker requirement with its associated requirement details.
 */
export function mergeWorkerRequirementWithIncludedResources(
  data: GetWorkerRequirementsResponse
): WorkerRequirement[] {
  const requirementMap = new Map<string, WorkerRequirementIncludedRequirementType>();
  const requirementMappingMap = new Map<
    string,
    WorkerRequirementIncludedRequirementMappingType[]
  >();
  const workerDocumentsMap = new Map<string, WorkerRequirementsIncludedDocumentsType[]>();
  const automatedNameUpdateMap = new Map<
    string,
    WorkerRequirementIncludedAutomatedNameUpdateType
  >();
  const prerequisiteActionMap = new Map<string, PrerequisiteAction>();

  data.included?.forEach((resource) => {
    switch (resource.type) {
      case API_TYPES.requirements: {
        requirementMap.set(resource.id, resource);
        break;
      }

      case API_TYPES.prerequisiteAction: {
        prerequisiteActionMap.set(resource.id, {
          id: resource.id,
          actionKey: resource.attributes.actionKey,
          displayName: resource.attributes.displayName,
          description: resource.attributes.description,
        });
        break;
      }

      case API_TYPES.requirementMappings: {
        const { requirementId } = resource.attributes;

        if (requirementMappingMap.has(requirementId)) {
          requirementMappingMap.get(requirementId)?.push(resource);
        } else {
          requirementMappingMap.set(requirementId, [resource]);
        }

        break;
      }

      case API_TYPES.workerDocuments: {
        const { requirementId } = resource.attributes;

        if (workerDocumentsMap.has(requirementId)) {
          workerDocumentsMap.get(requirementId)?.push(resource);
        } else {
          workerDocumentsMap.set(requirementId, [resource]);
        }

        break;
      }

      case API_TYPES.automatedNameUpdate: {
        const { documentId } = resource.attributes;

        automatedNameUpdateMap.set(documentId, resource);

        break;
      }

      default: {
        break;
      }
    }
  });

  return data.data.map((workerRequirement) => {
    const { attributes: workerRequirementAttributes } = workerRequirement;
    const { requirementId } = workerRequirementAttributes;
    const requirement = requirementMap.get(requirementId);
    const prerequisiteActionIds = requirement?.relationships?.prerequisiteActions?.data.map(
      (prerequisiteAction) => prerequisiteAction.id
    );
    const requirementMappings = requirementMappingMap.get(requirementId);

    const rawDocuments = workerDocumentsMap.get(requirementId);
    const documents = rawDocuments?.map((document) => ({
      ...document,
      automatedNameUpdate: automatedNameUpdateMap.get(document.id),
    }));

    return {
      ...workerRequirementAttributes,
      requirement,
      requirementMappings,
      documents,
      prerequisiteActions: prerequisiteActionIds
        ?.map((prerequisiteActionId) => prerequisiteActionMap.get(prerequisiteActionId))
        .filter((action): action is PrerequisiteAction => action !== undefined),
    };
  });
}
